import React from 'react';
import clsx from 'clsx';
import { endOfYear, startOfYear } from '@internationalized/date';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import { chunks, range } from './utils';
import { CalendarMonthOrYearCell } from './CalendarMonthOrYearCell';
import { CalendarState } from './useCalendarState';
import { useCustomDurationCalendarGrid } from './useCustomDurationCalendarGrid';

interface CalendarYearsGridProps<T extends string> {
  state: CalendarState<T>;
}

export function CalendarYearsGrid<T extends string>({
  state,
}: CalendarYearsGridProps<T>): JSX.Element {
  const years = range(
    state.visibleRange.start.year,
    state.visibleRange.end.year
  ).slice(0, 12);
  const yearRows = [...chunks(years, 3)];
  const { gridProps } = useCustomDurationCalendarGrid(
    { cellsDuration: 'years' },
    state
  );
  return (
    <table
      className={clsx(
        styles['sapphire-calendar__table'],
        styles['sapphire-calendar__table--month-year']
      )}
      {...gridProps}
    >
      <tbody>
        {yearRows.map((currentRowYears, rowIndex) => (
          <tr
            key={rowIndex}
            className={styles['sapphire-calendar__table-body-row']}
          >
            {currentRowYears.map((year, index) => {
              const newFocusedDate = state.focusedDate.set({
                year: year,
              });
              // Since validation is only about min and max dates, we just
              // check the edges of the period
              const isYearDisabled =
                state.isInvalid(startOfYear(newFocusedDate)) &&
                state.isInvalid(endOfYear(newFocusedDate)) &&
                state.isInvalid(newFocusedDate);
              return (
                <CalendarMonthOrYearCell
                  key={index}
                  date={newFocusedDate}
                  field={'year'}
                  isDisabled={isYearDisabled}
                  state={state}
                >
                  {year}
                </CalendarMonthOrYearCell>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
